import { requestPushToken, onForegroundPush } from "./firebase";
import RequestEngine from "./RequestEngine";
import { showInAppToast } from "./inAppToast";

/**
 * Asks for notification permission (a no-op prompt if already granted or
 * denied) and, on success, registers the token with the backend so this
 * browser starts receiving pushes for the logged-in merchant user.
 *
 * RequestEngine bakes sessionStorage's current token into a static axios
 * default header at construction time — it's never re-read afterward. This
 * module is imported (and evaluated) at initial bundle load, before login,
 * so a module-level instance would permanently capture an empty token. It
 * has to be constructed here, inside the function, so it's built fresh
 * after login has actually written the real token to sessionStorage.
 */
export async function registerPushToken() {
  try {
    const token = await requestPushToken();
    if (!token) {
      console.warn("Push notification registration skipped — no token.");
      return;
    }
    const engine = new RequestEngine();
    const response = await engine.saveItem("merchants-users/device-tokens", {
      token,
      platform: "web",
    });
    console.log("Push token registered with backend", response && response.status);
  } catch (err) {
    console.error("Push notification registration failed", err);
  }
}

// Anything that wants to react to an incoming push (the top-bar bell
// refreshing its unread badge, for one) subscribes here rather than each
// registering its own Firebase onMessage handler.
const arrivalListeners = new Set();

/**
 * Calls `listener` whenever a push arrives while the tab is open.
 * Returns an unsubscribe function.
 */
export function onNotificationArrived(listener) {
  arrivalListeners.add(listener);
  return () => arrivalListeners.delete(listener);
}

/** Shows a native notification for a push that arrives while the tab is focused. */
export function listenForForegroundPush() {
  onForegroundPush(async (payload) => {
    // web push is sent data-only (see push-notification.service.ts) so it's
    // always handled here rather than sometimes auto-displayed by the browser
    const data = payload.data || {};
    if (!data.title) return;

    arrivalListeners.forEach((listener) => {
      try {
        listener(data);
      } catch (err) {
        // one bad subscriber must not stop the notification showing
      }
    });

    // Always show the in-page banner: the OS toast below can be silently
    // swallowed by settings the app can't see, and this one can't be.
    showInAppToast({ title: data.title, body: data.body });

    const options = { body: data.body, icon: "/favicon.ico", data };
    try {
      // Prefer the service worker: `new Notification()` is the older path and
      // Chrome ignores or rejects it in several situations, while a
      // registration's showNotification() behaves consistently.
      const registration = await navigator.serviceWorker.ready;
      await registration.showNotification(data.title, options);
    } catch (err) {
      try {
        new Notification(data.title, options);
      } catch (fallbackErr) {
        console.error("Could not show foreground notification", fallbackErr);
      }
    }
  });
}
